import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title, CategoryScale, LinearScale, BarElement } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels'; // Import datalabels plugin
import { handleAuthError } from '../utils/auth'; // Import handleAuthError

ChartJS.register(ArcElement, Tooltip, Legend, Title, ChartDataLabels, CategoryScale, LinearScale, BarElement); // Register datalabels plugin and bar chart components

const MatchDetailPage = () => {
    const { matchId } = useParams();
    const navigate = useNavigate(); // Initialize navigate
    const [matchData, setMatchData] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState('wholeMatch'); // 'wholeMatch', 'setX', 'gameY'
    const [mistakesByPlayerChartData, setMistakesByPlayerChartData] = useState({});
    const [mistakesByTypeChartData, setMistakesByTypeChartData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMatchDetails = async () => {
            try {
                const token = localStorage.getItem('token'); // Retrieve JWT token
                if (!token) {
                    handleAuthError(navigate);
                    return;
                }

                const response = await fetch(`https://best3intennis.com/best3intennis-api/api/get_match_details.php?match_id=${matchId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}` // Send JWT token
                    },
                });

                if (!response.ok) {
                    const data = await response.json().catch(() => null);
                    if (data && (data.message?.includes('token') || data.error?.includes('token'))) {
                        handleAuthError(navigate);
                        return;
                    }
                    throw new Error('Failed to fetch match details.');
                }

                const data = await response.json();
                if (data.success) {
                    setMatchData(data.match);
                } else {
                    console.error('Failed to fetch match details:', data.message);
                    setError(data.message);
                }
            } catch (err) {
                if (err.message.includes('401')) {
                    handleAuthError(navigate);
                } else {
                    setError(err.message);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchMatchDetails();
    }, [matchId, navigate]);

    useEffect(() => {
        if (matchData) {
            const processChartData = () => {
                const player1Name = matchData.player1_name || 'Player 1';
                const player2Name = matchData.player2_name || 'Player 2';

                let player1TotalMistakes = 0;
                let player2TotalMistakes = 0;
                let player1MistakeDetails = {};
                let player2MistakeDetails = {};

                if (selectedFilter === 'wholeMatch') {
                    player1TotalMistakes = matchData.player1_total_mistakes || 0;
                    player2TotalMistakes = matchData.player2_total_mistakes || 0;
                    player1MistakeDetails = matchData.player1_total_mistakes_details || {};
                    player2MistakeDetails = matchData.player2_total_mistakes_details || {};
                } else if (selectedFilter.startsWith('set')) {
                    const setIndex = parseInt(selectedFilter.replace('set', '')) - 1;
                    const set = matchData.sets[setIndex];
                    if (set) {
                        player1TotalMistakes = set.player1_mistakes || 0;
                        player2TotalMistakes = set.player2_mistakes || 0;
                        player1MistakeDetails = set.player1_mistakes_details || {};
                        player2MistakeDetails = set.player2_mistakes_details || {};
                    }
                } else if (selectedFilter.startsWith('game')) {
                    const gameIdentifier = selectedFilter.replace('game', '');
                    const [targetSetIndex, targetGameIndex] = gameIdentifier.split('-').map(Number);

                    const set = matchData.sets[targetSetIndex];
                    if (set && set.games[targetGameIndex]) {
                        const game = set.games[targetGameIndex];
                        player1TotalMistakes = game.player1_mistakes || 0;
                        player2TotalMistakes = game.player2_mistakes || 0;
                        player1MistakeDetails = game.mistake_details?.player1_mistakes_details || {};
                        player2MistakeDetails = game.mistake_details?.player2_mistakes_details || {};
                    }
                }

                setMistakesByPlayerChartData({
                    labels: [player1Name, player2Name],
                    datasets: [
                        {
                            label: 'Total Mistakes',
                            data: [player1TotalMistakes, player2TotalMistakes],
                            backgroundColor: [
                                'rgba(75, 192, 192, 0.6)', // Color for Player 1
                                'rgba(255, 99, 132, 0.6)', // Color for Player 2
                            ],
                            borderColor: [
                                'rgba(75, 192, 192, 1)',
                                'rgba(255, 99, 132, 1)',
                            ],
                            borderWidth: 1,
                        },
                    ],
                });

                // Process data for Mistakes by Type and Player chart
                const allMistakeTypes = [...new Set([
                    ...Object.keys(player1MistakeDetails),
                    ...Object.keys(player2MistakeDetails)
                ])].sort();

                setMistakesByTypeChartData({
                    labels: allMistakeTypes,
                    datasets: [
                        {
                            label: player1Name,
                            data: allMistakeTypes.map(type => player1MistakeDetails[type] || 0),
                            backgroundColor: 'rgba(75, 192, 192, 0.6)',
                            borderColor: 'rgba(75, 192, 192, 1)',
                            borderWidth: 1,
                        },
                        {
                            label: player2Name,
                            data: allMistakeTypes.map(type => player2MistakeDetails[type] || 0),
                            backgroundColor: 'rgba(255, 99, 132, 0.6)',
                            borderColor: 'rgba(255, 99, 132, 1)',
                            borderWidth: 1,
                        },
                    ],
                });
            };
            processChartData();
        }
    }, [matchData, selectedFilter]);

    const mistakesByPlayerChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    font: {
                        size: 14,
                    },
                },
            },
            title: {
                display: true,
                text: 'Mistakes by Player',
                font: {
                    size: 18,
                },
            },
            datalabels: {
                color: '#fff',
                formatter: (value, context) => {
                    const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0%';
                    return `${value} (${percentage})`;
                },
                font: {
                    weight: 'bold',
                    size: 14,
                },
            },
        },
    };

    const mistakesByTypeChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    font: {
                        size: 14,
                    },
                },
            },
            title: {
                display: true,
                text: 'Mistakes by Type and Player',
                font: {
                    size: 18,
                },
            },
            datalabels: {
                color: '#000', // Changed to white for better visibility inside bars
                anchor: 'center', // Position label in the center of the bar
                align: 'center', // Align label in the center of the bar
                formatter: (value) => value > 0 ? value : '', // Only show labels for values > 0
                font: {
                    weight: 'bold',
                    size: 12,
                },
            },
        },
        scales: {
            x: {
                stacked: false, // Not stacked, grouped bars
                title: {
                    display: true,
                    text: 'Mistake Type',
                    font: {
                        size: 14,
                    },
                },
            },
            y: {
                stacked: false, // Not stacked, grouped bars
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Number of Mistakes',
                    font: {
                        size: 14,
                    },
                },
            },
        },
    };

    if (loading) {
        return <div>Loading match details...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    if (!matchData) {
        return <div>No match data found.</div>;
    }

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-3xl font-bold mb-6">Match Details: {matchData.player1_name} vs {matchData.player2_name}</h1>

                <div className="mb-4">
                    <label htmlFor="filter-dropdown" className="block text-lg font-medium text-gray-700">View Mistakes By:</label>
                    <select
                        id="filter-dropdown"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={selectedFilter}
                        onChange={(e) => setSelectedFilter(e.target.value)}
                    >
                        <option value="wholeMatch">Whole Match</option>
                        {matchData.sets && matchData.sets.map((set, setIndex) => (
                            <React.Fragment key={`set-fragment-${setIndex}`}>
                                <option value={`set${setIndex + 1}`}>Set {setIndex + 1}</option>
                                {set.games && set.games.map((game, gameIndex) => (
                                    <option key={`game-${setIndex}-${gameIndex}`} value={`game${setIndex}-${gameIndex}`}>
                                        &nbsp;&nbsp;&nbsp;Game {gameIndex + 1} (Set {setIndex + 1})
                                    </option>
                                ))}
                            </React.Fragment>
                        ))}
                    </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div className="bg-white p-6 rounded-lg shadow-md" style={{ height: '400px' }}>
                        {mistakesByPlayerChartData.labels && mistakesByPlayerChartData.labels.length > 0 && (mistakesByPlayerChartData.datasets[0].data[0] > 0 || mistakesByPlayerChartData.datasets[0].data[1] > 0) ? (
                            <Doughnut data={mistakesByPlayerChartData} options={mistakesByPlayerChartOptions} />
                        ) : (
                            <p>No data available for Mistakes by Player for the selected filter.</p>
                        )}
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-md" style={{ height: '400px' }}>
                        {mistakesByTypeChartData.labels && mistakesByTypeChartData.labels.length > 0 && (mistakesByTypeChartData.datasets[0].data.some(val => val > 0) || mistakesByTypeChartData.datasets[1].data.some(val => val > 0)) ? (
                            <Bar data={mistakesByTypeChartData} options={mistakesByTypeChartOptions} />
                        ) : (
                            <p>No data available for Mistakes by Type and Player for the selected filter.</p>
                        )}
                    </div>
                </div>

                <div className="mt-8">
                    <h2 className="text-2xl font-semibold mb-4">Match Summary</h2>
                    <p><strong>Date:</strong> {new Date(matchData.match_date).toLocaleDateString()}</p>
                    <p><strong>Score:</strong> {matchData.score}</p>
                    <p><strong>Winner:</strong> {matchData.winner_id === matchData.player1_id ? matchData.player1_name : matchData.player2_name}</p>
                    <p><strong>Total Mistakes ({matchData.player1_name}):</strong> {matchData.player1_total_mistakes || 0}</p>
                    {Object.keys(matchData.player1_total_mistakes_details).length > 0 && (
                        <ul className="list-disc list-inside ml-4">
                            {Object.entries(matchData.player1_total_mistakes_details).map(([type, count]) => (
                                <li key={type}>{type.replace(/_/g, ' ')}: {count}</li>
                            ))}
                        </ul>
                    )}
                    <p><strong>Total Mistakes ({matchData.player2_name}):</strong> {matchData.player2_total_mistakes || 0}</p>
                    {Object.keys(matchData.player2_total_mistakes_details).length > 0 && (
                        <ul className="list-disc list-inside ml-4">
                            {Object.entries(matchData.player2_total_mistakes_details).map(([type, count]) => (
                                <li key={type}>{type.replace(/_/g, ' ')}: {count}</li>
                            ))}
                        </ul>
                    )}

                    <h3 className="text-xl font-semibold mt-6 mb-3">Sets</h3>
                    {matchData.sets && matchData.sets.length > 0 ? (
                        matchData.sets.map((set, index) => (
                            <div key={index} className="border p-4 rounded-md mb-4">
                                <p className="font-bold">Set {index + 1}: {set.score}</p>
                                <p>Mistakes ({matchData.player1_name}): {set.player1_mistakes || 0}</p>
                                {Object.keys(set.player1_mistakes_details).length > 0 && (
                                    <ul className="list-disc list-inside ml-4">
                                        {Object.entries(set.player1_mistakes_details).map(([type, count]) => (
                                            <li key={type}>{type.replace(/_/g, ' ')}: {count}</li>
                                        ))}
                                    </ul>
                                )}
                                <p>Mistakes ({matchData.player2_name}): {set.player2_mistakes || 0}</p>
                                {Object.keys(set.player2_mistakes_details).length > 0 && (
                                    <ul className="list-disc list-inside ml-4">
                                        {Object.entries(set.player2_mistakes_details).map(([type, count]) => (
                                            <li key={type}>{type.replace(/_/g, ' ')}: {count}</li>
                                        ))}
                                    </ul>
                                )}
                                {set.games && set.games.length > 0 && (
                                    <div className="ml-4 mt-2">
                                        <h4 className="font-semibold">Games:</h4>
                                        {set.games.map((game, gameIndex) => (
                                            <div key={gameIndex} className="border-l-2 border-gray-200 pl-3 py-1">
                                                <p>Game {gameIndex + 1}: {game.score}</p>
                                                <p>Mistakes ({matchData.player1_name}): {game.player1_mistakes || 0}</p>
                                                {Object.keys(game.mistake_details?.player1_mistakes_details || {}).length > 0 && (
                                                    <ul className="list-disc list-inside ml-4">
                                                        {Object.entries(game.mistake_details.player1_mistakes_details).map(([type, count]) => (
                                                            <li key={type}>{type.replace(/_/g, ' ')}: {count}</li>
                                                        ))}
                                                    </ul>
                                                )}
                                                <p>Mistakes ({matchData.player2_name}): {game.player2_mistakes || 0}</p>
                                                {Object.keys(game.mistake_details?.player2_mistakes_details || {}).length > 0 && (
                                                    <ul className="list-disc list-inside ml-4">
                                                        {Object.entries(game.mistake_details.player2_mistakes_details).map(([type, count]) => (
                                                            <li key={type}>{type.replace(/_/g, ' ')}: {count}</li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <p>No sets recorded for this match.</p>
                    )}
                </div>
            </div>
    );
};

export default MatchDetailPage;


